/**
 * Email Service
 * Handles sending professional emails for various platform events
 * Emails are queued to Firestore and sent via Firebase Cloud Functions
 */

import { db } from '../config/firebase';
import { collection, addDoc, Timestamp } from 'firebase/firestore';

class EmailService {
  constructor() {
    this.emailQueue = collection(db, 'emailQueue');
  }

  normalizeDate(value) {
    if (!value) return null;
    if (value instanceof Date) return value;
    if (typeof value === 'string' || typeof value === 'number') {
      const parsed = new Date(value);
      return isNaN(parsed.getTime()) ? null : parsed;
    }
    if (typeof value.toDate === 'function') {
      try {
        return value.toDate();
      } catch (error) {
        console.warn('Unable to convert Firestore timestamp to Date:', error);
      }
    }
    if (typeof value.seconds === 'number') {
      return new Date(value.seconds * 1000);
    }
    return null;
  }

  formatSessionSchedule(dateValue, fallbackText = '') {
    const date = this.normalizeDate(dateValue);
    if (!date) return fallbackText || 'Session time to be announced';
    return date.toLocaleString(undefined, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  }

  /**
   * Queue an email to be sent
   * This adds the email to Firestore, which is processed by Firebase Cloud Functions
   */
  async queueEmail(emailData) {
    try {
      const email = {
        ...emailData,
        status: 'pending',
        attempts: 0,
        createdAt: Timestamp.now(),
        sentAt: null,
        error: null
      };

      const docRef = await addDoc(this.emailQueue, email);
      console.log('✅ Email queued successfully:', docRef.id);
      return { success: true, emailId: docRef.id };
    } catch (error) {
      console.error('❌ Error queueing email:', error);
      throw error;
    }
  }

  /**
   * Helper function to strip HTML tags for plain text
   */
  stripHtml(html) {
    if (!html) return '';
    return html
      .replace(/<style[^>]*>.*<\/style>/gm, '')
      .replace(/<script[^>]*>.*<\/script>/gm, '')
      .replace(/<[^>]+>/gm, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /**
   * Send instructor approval email
   */
  async sendInstructorApprovalEmail(instructorData) {
    const { name, email } = instructorData;

    const emailData = {
      to: email,
      subject: '🎉 Congratulations! Your Instructor Application is Approved',
      template: 'instructorApproval',
      templateData: {
        instructorName: name,
        platformName: 'Learn for Fun',
        dashboardUrl: `${window.location.origin}/instructor/dashboard`,
        supportEmail: 'info@learnforfun.online',
        year: new Date().getFullYear()
      },
      html: this.generateInstructorApprovalHTML(instructorData)
    };

    return await this.queueEmail(emailData);
  }

  /**
   * Send instructor rejection email
   */
  async sendInstructorRejectionEmail(instructorData, reason) {
    const { name, email } = instructorData;

    const emailData = {
      to: email,
      subject: 'Update on Your Instructor Application',
      template: 'instructorRejection',
      templateData: {
        instructorName: name,
        reason: reason || 'Unfortunately, we are unable to approve your application at this time.',
        reapplyUrl: `${window.location.origin}/become-instructor`,
        supportEmail: 'info@learnforfun.online',
        year: new Date().getFullYear()
      },
      html: this.generateInstructorRejectionHTML(instructorData, reason)
    };

    return await this.queueEmail(emailData);
  }

  /**
   * Send welcome email to new students
   */
  async sendWelcomeEmail(userData) {
    const { name, email } = userData;

    const emailData = {
      to: email,
      subject: '🚀 Welcome to Learn for Fun!',
      template: 'welcome',
      templateData: {
        userName: name,
        exploreUrl: `${window.location.origin}/sessions`,
        supportEmail: 'info@learnforfun.online',
        year: new Date().getFullYear()
      },
      html: this.generateWelcomeHTML(userData)
    };

    return await this.queueEmail(emailData);
  }

  /**
   * Send course enrollment confirmation
   */
  async sendEnrollmentConfirmation(enrollmentData) {
    const { studentEmail, studentName, courseName, instructorName } = enrollmentData;

    const emailData = {
      to: studentEmail,
      subject: `✅ Enrolled in ${courseName}`,
      template: 'enrollment',
      templateData: {
        studentName,
        courseName,
        instructorName,
        courseUrl: `${window.location.origin}/student/courses`,
        supportEmail: 'info@learnforfun.online',
        year: new Date().getFullYear()
      },
      html: this.generateEnrollmentHTML(enrollmentData)
    };

    return await this.queueEmail(emailData);
  }

  /**
   * Send reminder email for a specific session and student
   */
  async sendSessionReminderEmail(reminderData) {
    const {
      studentEmail,
      studentName,
      courseTitle,
      sessionTitle,
      sessionSchedule,
      sessionStartDate,
      sessionEndDate,
      classroomUrl,
      coachName,
      customMessage,
      sessionId
    } = reminderData;

    if (!studentEmail) {
      throw new Error('studentEmail is required to queue a reminder email');
    }

    const scheduleText =
      sessionSchedule ||
      this.formatSessionSchedule(sessionStartDate, sessionEndDate ? this.formatSessionSchedule(sessionEndDate) : '');

    const safeCustomMessage =
      typeof customMessage === 'string' ? customMessage.trim() : '';

    const emailData = {
      to: studentEmail,
      subject: `Reminder: ${sessionTitle || courseTitle || 'Upcoming session'}`,
      template: 'sessionReminder',
      templateData: {
        studentName,
        courseTitle,
        sessionTitle,
        sessionSchedule: scheduleText,
        classroomUrl,
        coachName,
        customMessage: safeCustomMessage,
        sessionId,
        dashboardUrl: `${window.location.origin}/student/sessions`,
        supportEmail: 'info@learnforfun.online',
        year: new Date().getFullYear()
      },
      html: this.generateSessionReminderHTML({
        studentName,
        courseTitle,
        sessionTitle,
        sessionSchedule: scheduleText,
        classroomUrl,
        coachName,
        customMessage: safeCustomMessage
      })
    };

    return await this.queueEmail(emailData);
  }

  /**
   * Send reminders to multiple students in bulk
   */
  async sendBulkSessionReminderEmails(reminderPayloads = []) {
    if (!Array.isArray(reminderPayloads) || reminderPayloads.length === 0) {
      return [];
    }

    return Promise.all(reminderPayloads.map((payload) => this.sendSessionReminderEmail(payload)));
  }

  /**
   * Generate professional HTML for instructor approval email
   */
  generateInstructorApprovalHTML(instructorData) {
    const { name } = instructorData;
    const dashboardUrl = `${window.location.origin}/instructor/dashboard`;
    const year = new Date().getFullYear();

    return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Instructor Approval</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { 
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      line-height: 1.6;
      color: #333;
      background-color: #f5f5f5;
    }
    .container {
      max-width: 600px;
      margin: 40px auto;
      background: white;
      border-radius: 12px;
      overflow: hidden;
      box-shadow: 0 4px 20px rgba(0,0,0,0.1);
    }
    .header {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      padding: 40px 30px;
      text-align: center;
    }
    .header h1 {
      font-size: 28px;
      margin-bottom: 10px;
    }
    .emoji {
      font-size: 60px;
      margin-bottom: 20px;
      display: block;
    }
    .content {
      padding: 40px 30px;
    }
    .greeting {
      font-size: 20px;
      color: #2c3e50;
      margin-bottom: 20px;
    }
    .message {
      font-size: 16px;
      color: #555;
      line-height: 1.8;
      margin-bottom: 30px;
    }
    .cta-button {
      display: inline-block;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      text-decoration: none;
      padding: 16px 40px;
      border-radius: 8px;
      font-weight: 600;
      font-size: 16px;
      margin: 20px 0;
      transition: transform 0.2s;
    }
    .cta-button:hover {
      transform: translateY(-2px);
    }
    .benefits {
      background: #f8f9fa;
      border-radius: 8px;
      padding: 25px;
      margin: 30px 0;
    }
    .benefits h3 {
      color: #2c3e50;
      margin-bottom: 15px;
      font-size: 18px;
    }
    .benefit-item {
      display: flex;
      align-items: flex-start;
      margin: 12px 0;
    }
    .benefit-icon {
      font-size: 24px;
      margin-right: 12px;
      min-width: 30px;
    }
    .benefit-text {
      color: #555;
      font-size: 15px;
    }
    .next-steps {
      border-left: 4px solid #667eea;
      padding-left: 20px;
      margin: 30px 0;
    }
    .next-steps h3 {
      color: #2c3e50;
      margin-bottom: 15px;
      font-size: 18px;
    }
    .step {
      margin: 10px 0;
      color: #555;
      font-size: 15px;
    }
    .step-number {
      display: inline-block;
      background: #667eea;
      color: white;
      width: 24px;
      height: 24px;
      border-radius: 50%;
      text-align: center;
      line-height: 24px;
      margin-right: 10px;
      font-size: 14px;
      font-weight: 600;
    }
    .footer {
      background: #f8f9fa;
      padding: 30px;
      text-align: center;
      border-top: 1px solid #e9ecef;
    }
    .footer-text {
      color: #7f8c8d;
      font-size: 14px;
      margin: 5px 0;
    }
    .social-links {
      margin: 20px 0;
    }
    .social-link {
      display: inline-block;
      margin: 0 10px;
      color: #667eea;
      text-decoration: none;
      font-size: 14px;
    }
    .divider {
      height: 1px;
      background: #e9ecef;
      margin: 30px 0;
    }
    @media only screen and (max-width: 600px) {
      .container { margin: 20px; }
      .header { padding: 30px 20px; }
      .content { padding: 30px 20px; }
      .header h1 { font-size: 24px; }
      .emoji { font-size: 50px; }
    }
  </style>
</head>
<body>
  <div class="container">
    <!-- Header -->
    <div class="header">
      <span class="emoji">🎉</span>
      <h1>Welcome to Our Instructor Community!</h1>
      <p>Your application has been approved</p>
    </div>

    <!-- Content -->
    <div class="content">
      <p class="greeting">Hi ${name},</p>
      
      <p class="message">
        Congratulations! We're thrilled to inform you that your instructor application has been <strong>approved</strong>! 
        You're now part of an elite community of educators who are shaping the future of skill-based learning.
      </p>

      <p class="message">
        Your expertise and passion for teaching will help inspire the next generation of creative thinkers and problem solvers. 
        We can't wait to see the amazing courses and sessions you'll create!
      </p>

      <!-- CTA Buttons -->
      <center>
        <a href="${dashboardUrl}" class="cta-button">
          🚀 Access Your Instructor Dashboard
        </a>
        <br>
        <a href="${window.location.origin}/sessions" class="cta-button" style="background: linear-gradient(135deg, #2ecc71 0%, #27ae60 100%); margin-top: 10px;">
          📚 View All Sessions
        </a>
      </center>

      <!-- Benefits -->
      <div class="benefits">
        <h3>✨ What You Can Do Now:</h3>
        <div class="benefit-item">
          <span class="benefit-icon">📚</span>
          <span class="benefit-text"><strong>Create Courses:</strong> Build comprehensive courses and sell them to thousands of students</span>
        </div>
        <div class="benefit-item">
          <span class="benefit-icon">🎓</span>
          <span class="benefit-text"><strong>Host Live Sessions:</strong> Conduct interactive coding sessions with real-time engagement</span>
        </div>
        <div class="benefit-item">
          <span class="benefit-icon">💰</span>
          <span class="benefit-text"><strong>Earn Revenue:</strong> Get 70% of all course sales directly in your account</span>
        </div>
        <div class="benefit-item">
          <span class="benefit-icon">📊</span>
          <span class="benefit-text"><strong>Track Analytics:</strong> Monitor your performance with detailed insights</span>
        </div>
        <div class="benefit-item">
          <span class="benefit-icon">🌟</span>
          <span class="benefit-text"><strong>Build Your Brand:</strong> Establish yourself as an expert in your field</span>
        </div>
      </div>

      <!-- Next Steps -->
      <div class="next-steps">
        <h3>🎯 Next Steps:</h3>
        <div class="step">
          <span class="step-number">1</span>
          <strong>Complete Your Profile:</strong> Add a professional photo, bio, and social links
        </div>
        <div class="step">
          <span class="step-number">2</span>
          <strong>Create Your First Course:</strong> Use our easy course builder to get started
        </div>
        <div class="step">
          <span class="step-number">3</span>
          <strong>Set Your Pricing:</strong> You control your course prices and earnings
        </div>
        <div class="step">
          <span class="step-number">4</span>
          <strong>Start Teaching:</strong> Publish and begin impacting students worldwide!
        </div>
      </div>

      <div class="divider"></div>

      <!-- Teaching Instructions -->
      <div class="next-steps">
        <h3>👨‍🏫 How to Conduct Your Classes:</h3>
        <div class="step">
          <span class="step-number">1</span>
          <strong>Prepare Your Materials:</strong> Have your code examples, slides, and hands-on exercises ready before class
        </div>
        <div class="step">
          <span class="step-number">2</span>
          <strong>Start with Engagement:</strong> Begin each session with an icebreaker or fun coding challenge to energize students
        </div>
        <div class="step">
          <span class="step-number">3</span>
          <strong>Teach Interactively:</strong> Use live coding, encourage questions, and have students code along with you
        </div>
        <div class="step">
          <span class="step-number">4</span>
          <strong>Provide Hands-On Practice:</strong> Give students time to work on exercises and projects during class
        </div>
        <div class="step">
          <span class="step-number">5</span>
          <strong>Encourage Collaboration:</strong> Foster peer learning through group activities and pair programming
        </div>
        <div class="step">
          <span class="step-number">6</span>
          <strong>Give Feedback:</strong> Review student work, provide constructive feedback, and celebrate their progress
        </div>
        <div class="step">
          <span class="step-number">7</span>
          <strong>End with a Recap:</strong> Summarize key learnings and assign practice exercises for next session
        </div>
        <div class="step">
          <span class="step-number">8</span>
          <strong>Stay Available:</strong> Answer student questions via email or platform chat between sessions
        </div>
      </div>

      <div class="divider"></div>

      <p class="message">
        Need help getting started? Our support team is here for you. 
        Reply to this email or visit our <a href="${window.location.origin}/help" style="color: #667eea;">Help Center</a>.
      </p>

      <p class="message">
        <strong>Welcome aboard! Let's make learning fun together! 🚀</strong>
      </p>
    </div>

    <!-- Footer -->
    <div class="footer">
      <p class="footer-text"><strong>Learn for Fun</strong></p>
      <p class="footer-text">Empowering young minds through interactive learning</p>
      
      <div class="social-links">
        <a href="${window.location.origin}" class="social-link">Website</a> •
        <a href="${window.location.origin}/marketplace" class="social-link">Marketplace</a> •
        <a href="${window.location.origin}/sessions" class="social-link">Sessions</a> •
        <a href="mailto:info@learnforfun.online" class="social-link">Support</a>
      </div>
      
      <p class="footer-text">© ${year} Learn for Fun. All rights reserved.</p>
      <p class="footer-text" style="font-size: 12px; margin-top: 15px; color: #95a5a6;">
        You're receiving this email because you applied to become an instructor on Learn for Fun.
      </p>
      
      <div style="background: #fff3cd; border: 1px solid #ffc107; border-radius: 8px; padding: 20px; margin-top: 20px; text-align: left;">
        <p style="font-size: 13px; font-weight: bold; color: #856404; margin-bottom: 10px;">⚖️ Legal Disclaimer</p>
        <p style="font-size: 11px; color: #856404; line-height: 1.6; margin-bottom: 8px;">
          The programs offered through this website are private tutoring and educational enrichment sessions designed to teach various skills including coding, technology, creative arts, and other educational topics to children. We are not a school, academy, childcare center, or state-licensed educational institution.
        </p>
        <p style="font-size: 11px; color: #856404; line-height: 1.6; margin-bottom: 8px;">
          Our classes are short-duration learning sessions and are provided strictly for educational purposes. They do not replace formal schooling and should not be interpreted as accredited academic instruction.
        </p>
        <p style="font-size: 11px; color: #856404; line-height: 1.6; margin-bottom: 8px;">
          We do not provide childcare, daycare services, or supervision beyond class hours. Parents or guardians are responsible for the care, safety, and supervision of their children outside the scheduled class time.
        </p>
        <p style="font-size: 11px; color: #856404; line-height: 1.6; margin-bottom: 0;">
          Participation in our programs is voluntary. Learning outcomes may vary based on student engagement and practice. All materials, lessons, and content provided are for instructional use only. By enrolling in our programs, parents/guardians acknowledge and accept these terms.
        </p>
      </div>
    </div>
  </div>
</body>
</html>
    `;
  }

  /**
   * Generate HTML for rejection email (professional and encouraging)
   */
  generateInstructorRejectionHTML(instructorData, reason) {
    const { name } = instructorData;
    const reapplyUrl = `${window.location.origin}/become-instructor`;
    const year = new Date().getFullYear();

    return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Application Update</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { 
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      line-height: 1.6;
      color: #333;
      background-color: #f5f5f5;
    }
    .container {
      max-width: 600px;
      margin: 40px auto;
      background: white;
      border-radius: 12px;
      overflow: hidden;
      box-shadow: 0 4px 20px rgba(0,0,0,0.1);
    }
    .header {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      padding: 40px 30px;
      text-align: center;
    }
    .content {
      padding: 40px 30px;
    }
    .message {
      font-size: 16px;
      color: #555;
      line-height: 1.8;
      margin-bottom: 20px;
    }
    .reason-box {
      background: #f8f9fa;
      border-left: 4px solid #e74c3c;
      padding: 20px;
      margin: 20px 0;
      border-radius: 4px;
    }
    .cta-button {
      display: inline-block;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      text-decoration: none;
      padding: 16px 40px;
      border-radius: 8px;
      font-weight: 600;
      font-size: 16px;
      margin: 20px 0;
    }
    .footer {
      background: #f8f9fa;
      padding: 30px;
      text-align: center;
      border-top: 1px solid #e9ecef;
    }
    .footer-text {
      color: #7f8c8d;
      font-size: 14px;
      margin: 5px 0;
    }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>Update on Your Application</h1>
    </div>
    
    <div class="content">
      <p class="message">Hi ${name},</p>
      
      <p class="message">
        Thank you for your interest in becoming an instructor on Learn for Fun. 
        We truly appreciate the time and effort you put into your application.
      </p>

      ${reason ? `
        <div class="reason-box">
          <strong>Feedback:</strong><br>
          ${reason}
        </div>
      ` : ''}

      <p class="message">
        We encourage you to continue developing your teaching skills and expertise. 
        You're welcome to reapply in the future when you feel ready.
      </p>

      <center>
        <a href="${reapplyUrl}" class="cta-button">Apply Again</a>
      </center>

      <p class="message">
        If you have any questions, please don't hesitate to reach out to our support team at 
        <a href="mailto:info@learnforfun.online">info@learnforfun.online</a>.
      </p>
    </div>

    <div class="footer">
      <p class="footer-text"><strong>Learn for Fun</strong></p>
      <p class="footer-text">© ${year} Learn for Fun. All rights reserved.</p>
    </div>
  </div>
</body>
</html>
    `;
  }

  /**
   * Generate welcome email HTML
   */
  generateWelcomeHTML(userData) {
    const { name } = userData;
    const exploreUrl = `${window.location.origin}/sessions`;
    const year = new Date().getFullYear();

    return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Welcome to Learn for Fun</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { 
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      background-color: #f5f5f5;
    }
    .container {
      max-width: 600px;
      margin: 40px auto;
      background: white;
      border-radius: 12px;
      overflow: hidden;
      box-shadow: 0 4px 20px rgba(0,0,0,0.1);
    }
    .header {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      padding: 40px 30px;
      text-align: center;
    }
    .emoji { font-size: 60px; margin-bottom: 20px; display: block; }
    .content { padding: 40px 30px; }
    .message { font-size: 16px; color: #555; line-height: 1.8; margin-bottom: 20px; }
    .cta-button {
      display: inline-block;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      text-decoration: none;
      padding: 16px 40px;
      border-radius: 8px;
      font-weight: 600;
      margin: 20px 0;
    }
    .footer {
      background: #f8f9fa;
      padding: 30px;
      text-align: center;
      border-top: 1px solid #e9ecef;
      color: #7f8c8d;
      font-size: 14px;
    }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <span class="emoji">🚀</span>
      <h1>Welcome to Learn for Fun!</h1>
    </div>
    
    <div class="content">
      <p class="message">Hi ${name},</p>
      <p class="message">
        Welcome aboard! We're excited to have you join our community of learners. 
        Get ready to explore amazing coding adventures, create awesome projects, and level up your skills!
      </p>
      <center>
        <a href="${exploreUrl}" class="cta-button">Start Exploring</a>
      </center>
    </div>

    <div class="footer">
      <p><strong>Learn for Fun</strong></p>
      <p>© ${year} Learn for Fun. All rights reserved.</p>
    </div>
  </div>
</body>
</html>
    `;
  }

  /**
   * Generate enrollment confirmation HTML
   */
  generateEnrollmentHTML(enrollmentData) {
    const { studentName, courseName, instructorName } = enrollmentData;
    const courseUrl = `${window.location.origin}/student/courses`;
    const year = new Date().getFullYear();

    return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Course Enrollment Confirmation</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { 
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      background-color: #f5f5f5;
    }
    .container {
      max-width: 600px;
      margin: 40px auto;
      background: white;
      border-radius: 12px;
      overflow: hidden;
      box-shadow: 0 4px 20px rgba(0,0,0,0.1);
    }
    .header {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      padding: 40px 30px;
      text-align: center;
    }
    .content { padding: 40px 30px; }
    .message { font-size: 16px; color: #555; line-height: 1.8; margin-bottom: 20px; }
    .course-box {
      background: #f8f9fa;
      padding: 20px;
      border-radius: 8px;
      margin: 20px 0;
    }
    .cta-button {
      display: inline-block;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      text-decoration: none;
      padding: 16px 40px;
      border-radius: 8px;
      font-weight: 600;
      margin: 20px 0;
    }
    .footer {
      background: #f8f9fa;
      padding: 30px;
      text-align: center;
      border-top: 1px solid #e9ecef;
      color: #7f8c8d;
      font-size: 14px;
    }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>✅ Enrollment Confirmed!</h1>
    </div>
    
    <div class="content">
      <p class="message">Hi ${studentName},</p>
      <p class="message">
        Congratulations! You're now enrolled in <strong>${courseName}</strong> by ${instructorName}.
      </p>
      
      <div class="course-box">
        <strong>Course:</strong> ${courseName}<br>
        <strong>Instructor:</strong> ${instructorName}
      </div>

      <center>
        <a href="${courseUrl}" class="cta-button">Start Learning</a>
      </center>
    </div>

    <div class="footer">
      <p><strong>Learn for Fun</strong></p>
      <p>© ${year} Learn for Fun. All rights reserved.</p>
    </div>
  </div>
</body>
</html>
    `;
  }

  generateSessionReminderHTML(reminderData) {
    const {
      studentName,
      courseTitle,
      sessionTitle,
      sessionSchedule,
      classroomUrl,
      coachName,
      customMessage
    } = reminderData;

    const actionUrl = classroomUrl || `${window.location.origin}/student/sessions`;
    const actionLabel = classroomUrl ? 'Join Live Classroom' : 'View Session Details';
    const greetingName = studentName || 'there';
    const instructorText = coachName || 'your instructor';
    const courseText = courseTitle || 'your upcoming class';

    const customMessageBlock = customMessage
      ? `<div class="custom-message"><p>${customMessage}</p></div>`
      : '';

    return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Session Reminder</title>
  <style>
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      background-color: #f5f5f5;
      color: #333;
      margin: 0;
      padding: 0;
    }
    .container {
      max-width: 640px;
      margin: 30px auto;
      background: #ffffff;
      border-radius: 14px;
      overflow: hidden;
      box-shadow: 0 14px 40px rgba(62, 64, 90, 0.12);
    }
    .header {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: #fff;
      padding: 32px 32px 26px;
      text-align: center;
    }
    .header h1 {
      margin: 0;
      font-size: 24px;
    }
    .content {
      padding: 35px 32px;
      line-height: 1.7;
    }
    .session-highlight {
      background: #f8f9ff;
      border-radius: 12px;
      padding: 18px 20px;
      margin: 20px 0;
      border: 1px solid #e0e7ff;
    }
    .cta-button {
      display: inline-block;
      padding: 14px 32px;
      border-radius: 10px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: #fff;
      text-decoration: none;
      font-weight: 600;
      margin-top: 10px;
    }
    .custom-message {
      background: #fff8e5;
      border-left: 4px solid #f7b500;
      padding: 16px;
      border-radius: 8px;
      margin: 20px 0;
    }
    .footer {
      padding: 24px 32px;
      background: #fafbff;
      text-align: center;
      font-size: 13px;
      color: #7a8299;
    }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>Upcoming Session Reminder</h1>
    </div>
    <div class="content">
      <p>Hi ${greetingName},</p>
      <p>This is a friendly reminder that ${sessionTitle ? `<strong>${sessionTitle}</strong>` : courseText} with ${instructorText} is coming up soon.</p>
      <div class="session-highlight">
        <p><strong>Course:</strong> ${courseText}</p>
        <p><strong>Schedule:</strong> ${sessionSchedule || 'See your dashboard for the exact time'}</p>
        ${classroomUrl ? `<p><strong>Classroom:</strong> <a href="${classroomUrl}" style="color:#667eea;">Join via this link</a></p>` : ''}
      </div>
      ${customMessageBlock}
      <p>Please make sure you have everything ready to participate. If you need to reschedule or have any questions, just reply to this email.</p>
      <a class="cta-button" href="${actionUrl}">${actionLabel}</a>
      <p style="margin-top: 25px;">We can't wait to see you in class! 🚀</p>
    </div>
    <div class="footer">
      <p>Learn for Fun • ${new Date().getFullYear()}</p>
      <p><a href="mailto:info@learnforfun.online" style="color: #667eea; text-decoration: none;">Contact Support</a></p>
    </div>
  </div>
</body>
</html>
    `;
  }

  generateHomeworkNotificationEmail(data) {
    const {
      studentName,
      homeworkTitle,
      homeworkDescription,
      courseTitle,
      sessionTitle,
      dueDateTime,
      homeworkUrl,
      customMessage
    } = data;

    return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>New Homework Assignment</title>
  <style>
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      background-color: #f5f5f5;
      color: #333;
      margin: 0;
      padding: 0;
    }
    .container {
      max-width: 640px;
      margin: 30px auto;
      background: #ffffff;
      border-radius: 14px;
      overflow: hidden;
      box-shadow: 0 14px 40px rgba(62, 64, 90, 0.12);
    }
    .header {
      background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
      color: #fff;
      padding: 32px 32px 26px;
      text-align: center;
    }
    .header h1 {
      margin: 0;
      font-size: 24px;
    }
    .content {
      padding: 35px 32px;
      line-height: 1.7;
    }
    .homework-box {
      background: #fff8e5;
      border-left: 4px solid #f7b500;
      border-radius: 12px;
      padding: 20px;
      margin: 20px 0;
    }
    .homework-box h3 {
      margin-top: 0;
      color: #333;
    }
    .due-date {
      background: #fff;
      border: 2px solid #f7b500;
      border-radius: 8px;
      padding: 12px 16px;
      margin: 15px 0;
      text-align: center;
      font-weight: 600;
      color: #d97706;
    }
    .cta-button {
      display: inline-block;
      padding: 14px 32px;
      border-radius: 10px;
      background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
      color: #fff;
      text-decoration: none;
      font-weight: 600;
      margin-top: 10px;
    }
    .footer {
      padding: 24px 32px;
      background: #fafbff;
      text-align: center;
      font-size: 13px;
      color: #7a8299;
    }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>📝 New Homework Assignment</h1>
    </div>
    <div class="content">
      <p>Hi ${studentName},</p>
      <p>A new homework assignment has been posted for your course.</p>
      <div class="homework-box">
        <h3>${homeworkTitle}</h3>
        <p><strong>Course:</strong> ${courseTitle}</p>
        ${sessionTitle ? `<p><strong>Session:</strong> ${sessionTitle}</p>` : ''}
        ${homeworkDescription ? `<p>${homeworkDescription}</p>` : ''}
        <div class="due-date">
          ⏰ Due: ${dueDateTime}
        </div>
      </div>
      ${customMessage ? `<div style="background: #e8f4f8; border-left: 4px solid #3498db; padding: 15px; margin: 20px 0; border-radius: 8px;">
        <p style="margin: 0; font-style: italic; color: #2c3e50;">${customMessage}</p>
      </div>` : ''}
      <div style="background: #f0f9ff; border: 2px solid #3b82f6; border-radius: 12px; padding: 20px; margin: 20px 0;">
        <h3 style="margin-top: 0; color: #1e40af;">📋 How to Complete & Submit Your Homework:</h3>
        <ol style="margin: 10px 0; padding-left: 20px; line-height: 1.8;">
          <li><strong>Log in</strong> to your student dashboard</li>
          <li><strong>Navigate</strong> to your registered sessions</li>
          <li><strong>Find</strong> this homework assignment under the "${sessionTitle}" session</li>
          <li><strong>Click</strong> "Submit Homework" button</li>
          <li><strong>Enter</strong> your answer in the text box</li>
          <li><strong>Upload</strong> any required files (if applicable)</li>
          <li><strong>Click</strong> "Submit" to complete your submission</li>
        </ol>
        <p style="margin: 10px 0 0 0; font-size: 14px; color: #64748b;">
          <strong>Note:</strong> Make sure to submit before the due date to avoid late penalties.
        </p>
      </div>
      <p>Please complete this assignment before the due date. If you have any questions, feel free to reach out to your instructor.</p>
      <center>
        <a href="${homeworkUrl || `${window.location.origin}/student/registered`}" class="cta-button">📝 View & Submit Homework</a>
      </center>
    </div>
    <div class="footer">
      <p>Learn for Fun • ${new Date().getFullYear()}</p>
      <p><a href="mailto:info@learnforfun.online" style="color: #667eea; text-decoration: none;">Contact Support</a></p>
    </div>
  </div>
</body>
</html>
    `;
  }

  async sendHomeworkNotification(data) {
    const html = this.generateHomeworkNotificationEmail(data);
    return this.queueEmail({
      to: data.studentEmail,
      toName: data.studentName,
      subject: data.subject || `📝 New Homework: ${data.homeworkTitle}`,
      html,
      text: this.stripHtml(html),
      templateParams: {
        student_name: data.studentName,
        homework_title: data.homeworkTitle,
        homework_description: data.homeworkDescription || '',
        course_title: data.courseTitle || '',
        session_title: data.sessionTitle || '',
        due_date: data.dueDate || '',
        due_date_time: data.dueDateTime || '',
        homework_url: data.homeworkUrl || '',
        custom_message: data.customMessage || ''
      }
    });
  }

  /**
   * Helper function to strip HTML tags for plain text
   */
  stripHtml(html) {
    if (!html) return '';
    return html
      .replace(/<style[^>]*>.*<\/style>/gm, '')
      .replace(/<script[^>]*>.*<\/script>/gm, '')
      .replace(/<[^>]+>/gm, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /**
   * Generate HTML for parent link notification email
   */
  generateParentLinkNotificationHTML(childName, parentName) {
    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Parent Account Linked</title>
</head>
<body style="margin: 0; padding: 0; font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; background-color: #f5f7fa;">
  <div style="max-width: 600px; margin: 0 auto; background-color: #ffffff;">
    <div class="header" style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 30px; text-align: center;">
      <h1 style="color: #ffffff; margin: 0; font-size: 28px;">👨‍👩‍👧‍👦 Parent Account Linked</h1>
    </div>
    <div class="content" style="padding: 30px;">
      <p style="font-size: 16px; line-height: 1.6; color: #333;">Hello ${childName},</p>
      <p style="font-size: 16px; line-height: 1.6; color: #333;">
        ${parentName} has successfully linked their parent account to your student account. 
        They can now view your progress, achievements, and upcoming sessions.
      </p>
      <div style="background-color: #f0f4ff; border-left: 4px solid #667eea; padding: 15px; margin: 20px 0; border-radius: 4px;">
        <p style="margin: 0; font-size: 14px; color: #4b5563;">
          <strong>What this means:</strong> Your parent can now see your learning progress, 
          badges earned, homework assignments, and session attendance. This helps them 
          support your learning journey!
        </p>
      </div>
      <p style="font-size: 16px; line-height: 1.6; color: #333;">
        If you did not authorize this link, please contact support immediately.
      </p>
      <center>
        <a href="${window.location.origin}/student" class="cta-button" style="display: inline-block; padding: 12px 30px; background-color: #667eea; color: #ffffff; text-decoration: none; border-radius: 8px; font-weight: bold; margin-top: 20px;">Go to Dashboard</a>
      </center>
    </div>
    <div class="footer" style="background-color: #f8f9fa; padding: 20px; text-align: center; border-top: 1px solid #e5e7eb;">
      <p style="margin: 0; font-size: 12px; color: #6b7280;">Learn for Fun • ${new Date().getFullYear()}</p>
      <p style="margin: 5px 0 0 0; font-size: 12px; color: #6b7280;">
        <a href="mailto:info@learnforfun.online" style="color: #667eea; text-decoration: none;">Contact Support</a>
      </p>
    </div>
  </div>
</body>
</html>
    `;
  }

  /**
   * Send parent link notification email
   */
  async sendParentLinkNotification(childEmail, childName, parentName) {
    const html = this.generateParentLinkNotificationHTML(childName, parentName);
    return this.queueEmail({
      to: childEmail,
      subject: '👨‍👩‍👧‍👦 Parent Account Linked to Your Profile',
      html
    });
  }

  /**
   * Generate HTML for weekly progress report email
   */
  generateWeeklyProgressReportHTML(reports, parentName) {
    const formatDate = (dateString) => {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    };

    const weekStart = formatDate(reports[0]?.weekStart);
    const weekEnd = formatDate(reports[0]?.weekEnd);

    let childrenHTML = '';
    reports.forEach((report, index) => {
      const engagementColor = report.engagementScore >= 80 ? '#10b981' : 
                             report.engagementScore >= 60 ? '#f59e0b' : '#ef4444';
      
      childrenHTML += `
        <div style="background: white; border: 1px solid #e5e7eb; border-radius: 12px; padding: 25px; margin-bottom: 20px;">
          <h3 style="margin: 0 0 15px 0; color: #667eea; font-size: 1.3rem;">${report.childName}</h3>
          
          <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 15px; margin-bottom: 20px;">
            <div style="background: #f0f4ff; padding: 15px; border-radius: 8px; text-align: center;">
              <div style="font-size: 2rem; margin-bottom: 5px;">⭐</div>
              <div style="font-size: 1.5rem; font-weight: bold; color: #667eea;">${report.weeklyPoints}</div>
              <div style="font-size: 0.85rem; color: #6b7280;">Points This Week</div>
            </div>
            
            <div style="background: #f0f4ff; padding: 15px; border-radius: 8px; text-align: center;">
              <div style="font-size: 2rem; margin-bottom: 5px;">🏆</div>
              <div style="font-size: 1.5rem; font-weight: bold; color: #667eea;">${report.weeklyBadges}</div>
              <div style="font-size: 0.85rem; color: #6b7280;">Badges Earned</div>
            </div>
            
            <div style="background: #f0f4ff; padding: 15px; border-radius: 8px; text-align: center;">
              <div style="font-size: 2rem; margin-bottom: 5px;">📚</div>
              <div style="font-size: 1.5rem; font-weight: bold; color: #667eea;">${report.sessionsAttended}/${report.sessionsScheduled}</div>
              <div style="font-size: 0.85rem; color: #6b7280;">Sessions Attended</div>
            </div>
            
            <div style="background: #f0f4ff; padding: 15px; border-radius: 8px; text-align: center;">
              <div style="font-size: 2rem; margin-bottom: 5px;">📊</div>
              <div style="font-size: 1.5rem; font-weight: bold; color: ${engagementColor};">${report.engagementScore}%</div>
              <div style="font-size: 0.85rem; color: #6b7280;">Engagement</div>
            </div>
          </div>

          ${report.badgesEarned.length > 0 ? `
            <div style="margin-bottom: 15px;">
              <h4 style="margin: 0 0 10px 0; color: #374151; font-size: 1rem;">🏅 Badges Earned This Week:</h4>
              <div style="display: flex; flex-wrap: wrap; gap: 10px;">
                ${report.badgesEarned.map(badge => `
                  <div style="background: #f9fafb; padding: 8px 12px; border-radius: 6px; display: inline-flex; align-items: center; gap: 8px;">
                    <span style="font-size: 1.2rem;">${badge.emoji || '🏅'}</span>
                    <span style="font-size: 0.9rem; color: #374151;">${badge.name || 'Badge'}</span>
                  </div>
                `).join('')}
              </div>
            </div>
          ` : ''}

          ${report.pointsBreakdown.length > 0 ? `
            <div style="margin-bottom: 15px;">
              <h4 style="margin: 0 0 10px 0; color: #374151; font-size: 1rem;">⭐ Points Breakdown:</h4>
              <div style="background: #f9fafb; padding: 12px; border-radius: 8px; font-size: 0.9rem;">
                ${report.pointsBreakdown.slice(0, 5).map(point => `
                  <div style="padding: 5px 0; border-bottom: 1px solid #e5e7eb;">
                    <strong>+${point.points}</strong> - ${point.reason || point.activityType || 'Activity'}
                  </div>
                `).join('')}
                ${report.pointsBreakdown.length > 5 ? `<div style="padding-top: 5px; color: #6b7280;">... and ${report.pointsBreakdown.length - 5} more</div>` : ''}
              </div>
            </div>
          ` : ''}

          <div style="background: #f0fdf4; border-left: 4px solid #10b981; padding: 12px; border-radius: 4px; margin-top: 15px;">
            <p style="margin: 0; font-size: 0.9rem; color: #065f46;">
              <strong>Total Points:</strong> ${report.totalPoints} | 
              <strong>Total Badges:</strong> ${report.totalBadges} | 
              <strong>Attendance Rate:</strong> ${report.attendanceRate}%
            </p>
          </div>
        </div>
      `;
    });

    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Weekly Progress Report</title>
</head>
<body style="margin: 0; padding: 0; font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; background-color: #f5f7fa;">
  <div style="max-width: 700px; margin: 0 auto; background-color: #ffffff;">
    <div class="header" style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 30px; text-align: center;">
      <h1 style="color: #ffffff; margin: 0; font-size: 28px;">📊 Weekly Progress Report</h1>
      <p style="color: #ffffff; margin: 10px 0 0 0; opacity: 0.9;">${weekStart} - ${weekEnd}</p>
    </div>
    <div class="content" style="padding: 30px;">
      <p style="font-size: 16px; line-height: 1.6; color: #333;">Hello ${parentName},</p>
      <p style="font-size: 16px; line-height: 1.6; color: #333;">
        Here's a summary of your ${reports.length === 1 ? 'child' : 'children'}'s learning progress this week:
      </p>
      
      ${childrenHTML}
      
      <div style="background: #f0f4ff; border-left: 4px solid #667eea; padding: 15px; margin: 20px 0; border-radius: 4px;">
        <p style="margin: 0; font-size: 14px; color: #4b5563;">
          <strong>💡 Tip:</strong> Encourage your child to maintain their learning streak! 
          Regular practice helps build strong coding skills.
        </p>
      </div>
      
      <center>
        <a href="https://learnforfun.online/parent" class="cta-button" style="display: inline-block; padding: 12px 30px; background-color: #667eea; color: #ffffff; text-decoration: none; border-radius: 8px; font-weight: bold; margin-top: 20px;">View Full Dashboard</a>
      </center>
    </div>
    <div class="footer" style="background-color: #f8f9fa; padding: 20px; text-align: center; border-top: 1px solid #e5e7eb;">
      <p style="margin: 0; font-size: 12px; color: #6b7280;">Learn for Fun • ${new Date().getFullYear()}</p>
      <p style="margin: 5px 0 0 0; font-size: 12px; color: #6b7280;">
        <a href="mailto:info@learnforfun.online" style="color: #667eea; text-decoration: none;">Contact Support</a>
      </p>
    </div>
  </div>
</body>
</html>
    `;
  }
}

const emailService = new EmailService();
export default emailService;
